import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react'
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
  User as FirebaseUser
} from 'firebase/auth'
import { app } from '../services/firebase'

interface User {
  id: string
  email: string
  name: string | null
  organization: string | null
  role: 'admin' | 'collaborator' | 'viewer'
  status: 'invited' | 'active' | 'deactivated'
}

// Why /api/auth/me rejected the current Firebase session, if it did.
// 'not_registered': valid Firebase account with no portal account (needs an invite)
// 'deactivated': portal account exists but has been deactivated by an admin
export type AccountStatus = 'not_registered' | 'deactivated' | null

interface AuthContextValue {
  user: User | null
  firebaseUser: FirebaseUser | null
  loading: boolean
  error: string | null
  accountStatus: AccountStatus
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: () => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  getToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accountStatus, setAccountStatus] = useState<AccountStatus>(null)

  const auth = getAuth(app)

  const fetchUser = useCallback(async (fbUser: FirebaseUser) => {
    try {
      const token = await fbUser.getIdToken()
      const response = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.ok) {
        setUser(await response.json())
        setAccountStatus(null)
        return
      }

      const body = await response.json().catch(() => ({}))
      if (body.code === 'not_registered') {
        setAccountStatus('not_registered')
      } else if (body.code === 'account_deactivated') {
        setAccountStatus('deactivated')
      } else {
        setAccountStatus(null)
      }
      setUser(null)
    } catch (err) {
      console.error('Failed to fetch user data:', err)
      setUser(null)
      setAccountStatus(null)
    }
  }, [])

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser)
      setLoading(true)

      if (fbUser) {
        await fetchUser(fbUser)
      } else {
        setUser(null)
        setAccountStatus(null)
      }

      setLoading(false)
    })

    return () => unsubscribe()
  }, [auth, fetchUser])

  const refreshUser = useCallback(async () => {
    const fbUser = auth.currentUser
    if (fbUser) {
      await fetchUser(fbUser)
    }
  }, [auth, fetchUser])

  const login = async (email: string, password: string) => {
    setError(null)
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } catch (err: any) {
      setError(err.message || 'Login failed')
    }
  }

  const loginWithGoogle = async () => {
    setError(null)
    try {
      const provider = new GoogleAuthProvider()
      await signInWithPopup(auth, provider)
    } catch (err: any) {
      setError(err.message || 'Google login failed')
    }
  }

  const logout = async () => {
    setError(null)
    try {
      await signOut(auth)
      setUser(null)
      setAccountStatus(null)
    } catch (err: any) {
      setError(err.message || 'Logout failed')
    }
  }

  const getToken = async (): Promise<string | null> => {
    if (!firebaseUser) return null
    return firebaseUser.getIdToken()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        loading,
        error,
        accountStatus,
        login,
        loginWithGoogle,
        logout,
        refreshUser,
        getToken
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
